import type { SoftDeletableEntity } from '@medusajs/medusa/dist/interfaces';
import type {
  Product as MedusaProduct,
  User as MedusaUser,
} from '@medusajs/medusa/dist/models';

/*
 * Core enums definitions from the stoux-backend library
 */
export enum ProductTemplateStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export enum ProductConditions {
  NEW = 'NEW',
  VERY_GOOD = 'VERY_GOOD',
  GOOD = 'GOOD',
  BAD = 'BAD',
  VERY_BAD = 'VERY_BAD',
}

/*
 * Core interfaces definitions from the stoux-backend library
 */
/**
 * Category interface
 * @type Category
 * @extends {SoftDeletableEntity}
 * @property {string} title - The title of the category
 * @property {string} thumbnail - The thumbnail of the category
 * @property {string} handle - The handle of the category
 * @property {string} parentId - The parent id of the category
 */
export type Category = {
  title: string;
  thumbnail: string | null;
  handle: string;
  parentId: string | null;
  mpath: string | null;
} & InstanceType<typeof SoftDeletableEntity>;

/**
 * ProductTemplate interface
 * @type ProductTemplate
 * @extends {SoftDeletableEntity}
 * @property {string} title - The title of the product template
 * @property {string} handle - The handle of the product template
 * @property {string} barcode - The barcode of the product template
 * @property {string} categoryId - The category id of the product template
 * @property {Record<string, unknown>} metadata - The metadata of the product template
 * @property {ProductTemplateStatus} status - The status of the product template
 */
export type ProductTemplate = {
  title: string;
  handle: string;
  barcode: string | null;
  thumbnail: string | null;
  categoryId: string | null;
  metadata: Record<string, unknown> | null;
  status: ProductTemplateStatus;
} & InstanceType<typeof SoftDeletableEntity>;

/**
 * ProductTemplateHasRating interface
 * @type ProductTemplateHasRating
 * @extends {SoftDeletableEntity}
 * @property {string} comment - The comment of the product template rating
 * @property {number} rating - The rating of the product template rating
 * @property {string[]} images - The images of the product template rating
 * @property {string} thumbnail - The thumbnail of the product template rating
 * @property {string} templateId - The template id of the product template rating
 * @property {string} customerId - The customer id of the product template rating
 */
export type ProductTemplateHasRating = {
  comment: string | null;
  rating: number;
  images: string[];
  thumbnail: string | null;
  templateId: string;
  customerId: string;
} & InstanceType<typeof SoftDeletableEntity>;

/**
 * Product interface
 * @type Product
 * @extends {MedusaProduct}
 * @property {string} serialNumber - The serial number of the product
 * @property {string} storeId - The store id of the product
 * @property {string} templateId - The template id of the product
 */
export type Product = {
  serialNumber: string | null;
  storeId: string;
  templateId: string; // represents the product template id
  productCondition: ProductConditions;
} & InstanceType<typeof MedusaProduct>;

/**
 * User interface
 * @type User
 * @extends {MedusaUser}
 * @property {string} storeId - The store id of the user, can be null if the user do not have a store yet
 */
export type User = {
  storeId: string | null;
} & InstanceType<typeof MedusaUser>;

export type WithSearchQueryRes = {
  offset: number;
  limit: number;
};

export type WithCountRes = {
  count: number;
};
